<template>
  <div>
    <a-alert
      type="info"
      show-icon
      :message="$t('appreciation.automatic')"
      :description="$t('appreciation.automaticDescriptionCollege')"
      style="margin-bottom: 16px"
    />

    <a-card size="small" style="margin-bottom: 16px">
      <a-form>
        <a-row :gutter="16">
          <a-col :span="8">
            <a-form-item :label="$t('etablissement.niveau')">
              <a-select
                v-model="filters.level"
                style="width: 100%"
                :placeholder="$t('all.select')"
                @change="handleLevelChange"
              >
                <a-select-option
                  v-for="(value, name) in levelSelectData"
                  :key="name"
                  :value="name"
                >
                  {{ value }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="8">
            <a-form-item :label="$t('college.bulletin.config.trimestres')">
              <a-select
                v-model="filters.trimester"
                style="width: 100%"
                :placeholder="$t('college.bulletin.config.trimestres')"
                @change="handleTrimesterChange"
              >
                <a-select-option value="1">1</a-select-option>
                <a-select-option value="2">2</a-select-option>
                <a-select-option value="3">3</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="8">
            <a-form-item :label="$t('emploi.matiere')">
              <a-select
                v-model="filters.subjectId"
                show-search
                option-filter-prop="children"
                style="width: 100%"
                :placeholder="$t('emploi.choisirMatiere')"
                :filter-option="filterOption"
                :loading="loadingSubjects"
                @change="handleSubjectChange"
              >
                <a-select-option
                  v-for="s in subjectOptions"
                  :key="s._id"
                  :value="s._id"
                >
                  {{ s.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div
      class="mb-2"
      style="
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
      "
    >
      <a-button
        icon="plus"
        type="primary"
        @click="showAddModal"
        :disabled="!hasValidFilters || isReadOnly"
      >
        {{ $t("all.add") }}
      </a-button>
      <div style="display: flex; gap: 8px; align-items: center">
        <a-button
          type="primary"
          icon="check"
          @click="applyAutomaticAppreciation"
          :disabled="!hasValidFilters || appreciations.length === 0 || isReadOnly"
        >
          {{ $t("appreciation.applyAutomatic") }}
        </a-button>
        <a-button type="primary" @click="handleClone" :disabled="isReadOnly">
          {{ $t("action.clone") }}
        </a-button>
      </div>
    </div>

    <div class="card">
      <a-table
        :rowKey="'_id'"
        :columns="columns"
        :data-source="appreciations"
        :pagination="false"
        :loading="loading"
      >
        <template slot="min" slot-scope="text, record">
          {{ record.min }}
        </template>
        <template slot="max" slot-scope="text, record">
          {{ record.max }}
        </template>
        <template slot="appreciation" slot-scope="text, record">
          {{ record.appreciation }}
        </template>
        <template slot="operation" slot-scope="text, record">
          <div style="display: flex; gap: 8px; align-items: center">
            <a-button
              type="primary"
              icon="edit"
              @click="showEditModal(record)"
              :disabled="isReadOnly"
            >
              {{ $t("action.modifier") }}
            </a-button>
            <a-button
              type="danger"
              icon="delete"
              @click="removeAppreciation(record._id)"
              :disabled="isReadOnly"
            >
              {{ $t("action.supprimer") }}
            </a-button>
          </div>
        </template>
      </a-table>
    </div>

    <!-- Add/Edit Modal -->
    <a-modal
      :visible="visibleModal"
      :title="editingAppreciation ? $t('action.modifier') : $t('all.add')"
      @cancel="handleCancel"
      :footer="null"
    >
      <a-form :form="form" @submit="handleSubmit">
        <a-form-item :label="$t('certification.min')">
          <a-input-number
            v-decorator="[
              'min',
              {
                rules: [{ required: true, message: minRequiredMessage }],
                initialValue: editingAppreciation ? editingAppreciation.min : 0,
              },
            ]"
            :min="0"
            :max="20"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item :label="$t('certification.max')">
          <a-input-number
            v-decorator="[
              'max',
              {
                rules: [{ required: true, message: maxRequiredMessage }],
                initialValue: editingAppreciation ? editingAppreciation.max : 20,
              },
            ]"
            :min="0"
            :max="20"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item :label="$t('appreciation.appreciation')">
          <a-select
            v-decorator="[
              'appreciationPredefined',
              {
                initialValue:
                  editingAppreciation &&
                  predefinedAppreciations.find(
                    (a) => a.appreciation === editingAppreciation.appreciation
                  )
                    ? editingAppreciation.appreciation
                    : null,
              },
            ]"
            show-search
            option-filter-prop="children"
            :placeholder="$t('appreciation.selectPredefinedAppreciation')"
            :filter-option="filterOption"
            allow-clear
            @change="handlePredefinedChange"
          >
            <a-select-option
              v-for="option in predefinedAppreciations"
              :key="option.id"
              :value="option.appreciation"
            >
              {{ option.appreciation }}
            </a-select-option>
          </a-select>

          <a-divider orientation="center">{{ $t("all.or") }}</a-divider>

          <a-input
            v-decorator="[
              'appreciationCustom',
              {
                initialValue:
                  editingAppreciation &&
                  !predefinedAppreciations.find(
                    (a) => a.appreciation === editingAppreciation.appreciation
                  )
                    ? editingAppreciation.appreciation
                    : '',
              },
            ]"
            :placeholder="$t('appreciation.customAppreciation')"
            @change="handleCustomChange"
          />
        </a-form-item>
        <div class="form-actions mt-0">
          <a-button
            type="primary"
            htmlType="submit"
            :loading="saving"
            :disabled="isReadOnly"
            class="mr-3"
          >
            {{ $t("action.enregistrer") }}
          </a-button>
          <a-button key="back" @click="handleCancel">
            {{ $t("action.annuler") }}
          </a-button>
        </div>
      </a-form>
    </a-modal>

    <!-- Existing Appreciations Modal (before apply) -->
    <a-modal
      :visible="visibleExistingModal"
      :title="$t('appreciation.existingAppreciations')"
      @cancel="handleCancelExistingModal"
      width="70%"
      :footer="null"
    >
      <div v-if="loadingExisting" class="text-center p-4">
        <a-spin />
      </div>
      <div v-else-if="existingAppreciations.length === 0" class="text-center p-4">
        <p>{{ $t("appreciation.noExistingAppreciations") }}</p>
        <a-button
          type="primary"
          class="mt-3"
          @click="confirmApplyAppreciations"
          :disabled="isReadOnly"
        >
          {{ $t("appreciation.applyAutomatic") }}
        </a-button>
      </div>
      <div v-else>
        <p class="mb-3">{{ $t("appreciation.selectToReplace") }}</p>
        <a-table
          :rowKey="'_id'"
          :columns="existingColumns"
          :data-source="existingAppreciations"
          :pagination="{ pageSize: 10 }"
          :row-selection="{
            selectedRowKeys: selectedExistingIds,
            onChange: onSelectExisting,
          }"
        >
          <template slot="student" slot-scope="text, record">
            <span v-if="record.student">
              {{ record.student.firstName }} {{ record.student.lastName }}
            </span>
          </template>
          <template slot="classroom" slot-scope="text, record">
            <span v-if="record.classroom">{{ record.classroom.name }}</span>
          </template>
        </a-table>
        <div class="mt-4 text-right">
          <a-button @click="handleCancelExistingModal" class="mr-2">
            {{ $t("action.annuler") }}
          </a-button>
        <a-button
          type="primary"
          @click="confirmApplyAppreciations"
          :loading="applying"
          :disabled="isReadOnly"
        >
          {{ $t("all.apply") }}
        </a-button>
        </div>
      </div>
    </a-modal>

    <!-- Clone modal -->
    <a-modal
      :visible="visibleCloneModal"
      centered
      @cancel="handleCloneCancel"
      width="70%"
      :footer="null"
    >
      <template slot="title">
        <div class="text-center">
          <h4 class="mb-0">{{ $t("appreciation.duplicateAppreciations") }}</h4>
        </div>
      </template>

      <div class="p-3">
        <div class="mb-4">
          <a-divider orientation="left">{{ $t("all.source") }}</a-divider>
          <p class="text-muted small mb-3">{{ $t("appreciation.cloneCollegeSourceHint") }}</p>
          <a-row :gutter="16">
            <a-col :span="6">
              <a-form-item :label="$t('etablissement.niveau')">
                <a-select
                  v-model="cloneSource.level"
                  style="width: 100%"
                  :placeholder="$t('all.select')"
                  @change="onCloneSourceLevelTrimChange"
                >
                  <a-select-option
                    v-for="(value, name) in levelSelectData"
                    :key="name"
                    :value="name"
                  >
                    {{ value }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item :label="$t('college.bulletin.config.trimestres')">
                <a-select
                  v-model="cloneSource.trimester"
                  style="width: 100%"
                  @change="onCloneSourceLevelTrimChange"
                >
                  <a-select-option value="1">1</a-select-option>
                  <a-select-option value="2">2</a-select-option>
                  <a-select-option value="3">3</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item :label="$t('emploi.matiere')">
                <a-select
                  v-model="cloneSource.subjectId"
                  show-search
                  option-filter-prop="children"
                  style="width: 100%"
                  :placeholder="$t('emploi.choisirMatiere')"
                  :filter-option="filterOption"
                  :loading="loadingCloneSourceSubjects"
                >
                  <a-select-option
                    v-for="s in cloneSourceSubjectOptions"
                    :key="s._id"
                    :value="s._id"
                  >
                    {{ s.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item :label="$t('scolarite.schoolYear')">
                <a-select
                  v-model="cloneSource.schoolYear"
                  style="width: 100%"
                  :placeholder="$t('scolarite.selectSchoolYear')"
                >
                  <a-select-option
                    v-for="sy in schoolYears"
                    :key="sy._id"
                    :value="sy._id"
                  >
                    {{ sy.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
        </div>

        <div class="mb-4">
          <a-divider orientation="left">{{ $t("all.targets") }}</a-divider>
          <p class="text-muted small mb-3">{{ $t("appreciation.cloneCollegeTargetHint") }}</p>
          <a-row :gutter="16" class="mb-2">
            <a-col :span="8">
              <div class="clone-target-select-all">
                <a-button
                  class="w-100"
                  :loading="selectingAllCloneTargets"
                  :disabled="isReadOnly"
                  @click="selectAllCloneTargets"
                >
                  {{ $t("sms.selectAll") }}
                </a-button>
              </div>
            </a-col>
          </a-row>
          <a-row :gutter="16">
            <a-col :span="8">
              <a-form-item :label="$t('scolarite.schoolYear')">
                <a-select
                  v-model="cloneTarget.schoolYearIds"
                  mode="multiple"
                  style="width: 100%"
                  :placeholder="$t('scolarite.selectSchoolYear')"
                  :maxTagCount="2"
                >
                  <a-select-option
                    v-for="sy in schoolYears"
                    :key="sy._id"
                    :value="sy._id"
                  >
                    {{ sy.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item :label="$t('etablissement.niveau')">
                <a-select
                  v-model="cloneTarget.levels"
                  mode="multiple"
                  style="width: 100%"
                  :placeholder="$t('all.select')"
                  :maxTagCount="2"
                  @change="onCloneTargetLevelTrimChange"
                >
                  <a-select-option
                    v-for="(value, name) in levelSelectData"
                    :key="name"
                    :value="String(name)"
                  >
                    {{ value }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item :label="$t('college.bulletin.config.trimestres')">
                <a-select
                  v-model="cloneTarget.trimesters"
                  mode="multiple"
                  style="width: 100%"
                  :placeholder="$t('college.bulletin.config.trimestres')"
                  :maxTagCount="3"
                  @change="onCloneTargetLevelTrimChange"
                >
                  <a-select-option value="1">1</a-select-option>
                  <a-select-option value="2">2</a-select-option>
                  <a-select-option value="3">3</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="16">
            <a-col :span="24">
              <a-form-item :label="$t('emploi.matiere')">
                <a-select
                  v-model="cloneTarget.subjectIds"
                  mode="multiple"
                  show-search
                  option-filter-prop="children"
                  style="width: 100%"
                  :placeholder="$t('appreciation.selectTargetSubjects')"
                  :filter-option="filterOption"
                  :loading="loadingCloneTargetSubjects"
                >
                  <a-select-option
                    v-for="s in cloneTargetSubjectOptions"
                    :key="s._id"
                    :value="s._id"
                  >
                    {{ s.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 offset-md-8">
            <a-button
              type="primary"
              class="w-100"
              :loading="cloning"
              :disabled="isReadOnly"
              @click="executeClone"
            >
              <a-icon type="save" />
              {{ $t("all.apply") }}
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState, mapGetters } from "vuex";
import { getLevels } from "@/config/LevelConstants";
import { getAvailableLevels } from "@/utilities/settings";
import moment from "moment";

const BASE = "/bulletin/college/auto-appreciation";

export default {
  name: "AutomaticAppreciationCollege",
  beforeMount() {
    this.form = this.$form.createForm(this, { name: "AppreciationCollegeForm" });
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadingSubjects: false,
      visibleModal: false,
      editingAppreciation: null,
      form: this.$form.createForm(this),
      filters: {
        level: null,
        trimester: "1",
        subjectId: null,
      },
      configSubjects: [],
      appreciations: [],
      schoolType: null,
      visibleExistingModal: false,
      existingAppreciations: [],
      selectedExistingIds: [],
      loadingExisting: false,
      applying: false,
      predefinedAppreciations: [],
      visibleCloneModal: false,
      schoolYearsList: [],
      cloneSource: {
        level: null,
        trimester: "1",
        subjectId: null,
        schoolYear: null,
      },
      cloneTarget: {
        schoolYearIds: [],
        levels: [],
        trimesters: [],
        subjectIds: [],
      },
      cloneSourceConfigSubjects: [],
      cloneTargetConfigSubjects: [],
      loadingCloneSourceSubjects: false,
      loadingCloneTargetSubjects: false,
      selectingAllCloneTargets: false,
      cloning: false,
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    ...mapGetters("settings", ["packDetailsGetter"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode || "TN";
    },
    schoolDetails() {
      return (
        this.user.building.find(
          (el) => el.dbName == this.settings.activeBuilding
        ) || {}
      );
    },
    levelSelectData() {
      return getLevels(
        this.$t.bind(this),
        this.secondarySchoolTypes,
        this.countryCode
      );
    },
    secondarySchoolTypes() {
      if (!Array.isArray(this.schoolType)) return [];
      const allowedTypes = ["college", "lycee", "junior", "senior"];
      return this.schoolType.filter((type) => allowedTypes.includes(type));
    },
    isReadOnly() {
      const value = this.packDetailsGetter?.access?.edManager?.autoAppreciation;
      return value === "ro";
    },
    schoolYears() {
      return this.schoolYearsList.map((sy) => ({
        _id: sy._id,
        label: this.formatSchoolYear(sy),
      }));
    },
    cloneSourceSubjectOptions() {
      return this.mapConfigToSubjectOptions(this.cloneSourceConfigSubjects);
    },
    cloneTargetSubjectOptions() {
      return this.mapConfigToSubjectOptions(this.cloneTargetConfigSubjects);
    },
    hasValidFilters() {
      return (
        this.filters.level != null &&
        this.filters.trimester &&
        this.filters.subjectId
      );
    },
    subjectOptions() {
      return this.mapConfigToSubjectOptions(this.configSubjects);
    },
    minRequiredMessage() {
      return this.$t("requis.min");
    },
    maxRequiredMessage() {
      return this.$t("requis.max");
    },
    columns() {
      return [
        { title: this.$t("certification.min"), key: "min", scopedSlots: { customRender: "min" } },
        { title: this.$t("certification.max"), key: "max", scopedSlots: { customRender: "max" } },
        { title: this.$t("appreciation.appreciation"), key: "appreciation", scopedSlots: { customRender: "appreciation" } },
        { title: this.$t("action.operation"), key: "operation", align: "center", scopedSlots: { customRender: "operation" } },
      ];
    },
    existingColumns() {
      return [
        { title: this.$t("liste.nomPrenom"), key: "student", scopedSlots: { customRender: "student" } },
        { title: this.$t("etablissement.classe"), key: "classroom", scopedSlots: { customRender: "classroom" } },
        { title: this.$t("appreciation.appreciation"), dataIndex: "appreciation", key: "appreciation" },
      ];
    },
  },
  watch: {
    "filters.level"() {
      this.filters.subjectId = null;
      this.configSubjects = [];
      this.appreciations = [];
      if (this.filters.level != null && this.filters.trimester) {
        this.loadConfigSubjects();
      }
    },
    "filters.trimester"() {
      this.filters.subjectId = null;
      this.configSubjects = [];
      this.appreciations = [];
      if (this.filters.level != null && this.filters.trimester) {
        this.loadConfigSubjects();
      }
    },
    "filters.subjectId"() {
      if (this.filters.subjectId && this.settings.activeSchoolarYear) {
        this.loadAutoAppreciations();
      } else {
        this.appreciations = [];
      }
    },
  },
  async created() {
    this.loading = true;
    try {
      this.schoolType = this.activeBuilding && this.activeBuilding.type;
      const buildingRes = await apiClient.post("/buildings/filter", {
        query: { _id: this.settings.activeBuildingId },
      });
      const building = buildingRes.data[0] || {};
      this.predefinedAppreciations = building.appreciationOptions || [];
      if (Array.isArray(this.secondarySchoolTypes) && this.secondarySchoolTypes.length) {
        const levels = getAvailableLevels(
          this.$t.bind(this),
          this.secondarySchoolTypes,
          this.countryCode
        );
        if (levels.length) this.filters.level = String(levels[0]);
      }
      this.filters.trimester = "1";

      if (this.filters.level && this.filters.trimester) {
        await this.loadConfigSubjects();
      }
    } catch (e) {
      console.error(e);
      this.$message.error(this.$t("error.erreur"));
    } finally {
      this.loading = false;
    }
  },
  methods: {
    cloneObjectIdString(val) {
      if (val == null || val === "") return "";
      if (typeof val === "object") {
        if (val._id != null) return String(val._id).trim();
        if (val.$oid != null) return String(val.$oid).trim();
      }
      return String(val).trim();
    },
    mapConfigToSubjectOptions(list) {
      return (list || []).map((s) => ({
        _id: s.subject && (s.subject._id || s.subject),
        name: (s.subject && (s.subject.name || s.subject.customName)) || "-",
      }));
    },
    formatSchoolYear(sy) {
      if (!sy || !sy.start || !sy.end) return "";
      const start = moment(sy.start).format("YYYY");
      const end = moment(sy.end).format("YYYY");
      return `${start}/${end}`;
    },
    async fetchSchoolYears() {
      try {
        const response = await apiClient.post("/schoolaryears/filter", {
          query: {},
        });
        this.schoolYearsList = (response.data || []).map((elem) => ({
          ...elem,
          key: elem._id,
        }));
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t("error.erreur"));
      }
    },
    async loadCloneSourceSubjects() {
      if (!this.cloneSource.level || !this.cloneSource.trimester) {
        this.cloneSourceConfigSubjects = [];
        return;
      }
      this.loadingCloneSourceSubjects = true;
      try {
        const res = await apiClient.post("/bulletin/college/config/subjects", {
          level: Number(this.cloneSource.level),
          trimester: Number(this.cloneSource.trimester),
        });
        this.cloneSourceConfigSubjects = res.data || [];
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
        this.cloneSourceConfigSubjects = [];
      } finally {
        this.loadingCloneSourceSubjects = false;
      }
    },
    mergeConfigSubjectRows(results) {
      const byId = new Map();
      for (const res of results || []) {
        for (const row of res.data || []) {
          const sid = row.subject && (row.subject._id || row.subject);
          if (!sid) continue;
          const key = String(sid);
          if (!byId.has(key)) byId.set(key, row);
        }
      }
      return Array.from(byId.values());
    },
    async loadCloneTargetSubjects() {
      const levels = (this.cloneTarget.levels || []).filter(
        (l) => l != null && l !== ""
      );
      const trimesters = (this.cloneTarget.trimesters || []).filter(
        (t) => t != null && t !== ""
      );
      if (!levels.length || !trimesters.length) {
        this.cloneTargetConfigSubjects = [];
        return;
      }
      this.loadingCloneTargetSubjects = true;
      try {
        const tasks = [];
        for (const level of levels) {
          for (const trim of trimesters) {
            tasks.push(
              apiClient.post("/bulletin/college/config/subjects", {
                level: Number(level),
                trimester: Number(trim),
              })
            );
          }
        }
        const results = await Promise.all(tasks);
        this.cloneTargetConfigSubjects = this.mergeConfigSubjectRows(results);
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
        this.cloneTargetConfigSubjects = [];
      } finally {
        this.loadingCloneTargetSubjects = false;
      }
    },
    onCloneSourceLevelTrimChange() {
      this.cloneSource.subjectId = null;
      this.loadCloneSourceSubjects();
    },
    onCloneTargetLevelTrimChange() {
      this.cloneTarget.subjectIds = [];
      this.loadCloneTargetSubjects();
    },
    async handleClone() {
      this.visibleCloneModal = true;
      this.cloneSource = {
        level: this.filters.level,
        trimester: this.filters.trimester || "1",
        subjectId: this.filters.subjectId,
        schoolYear: this.settings.activeSchoolarYear || null,
      };
      this.cloneTarget = {
        schoolYearIds: this.settings.activeSchoolarYear
          ? [this.settings.activeSchoolarYear]
          : [],
        levels:
          this.filters.level != null && this.filters.level !== ""
            ? [String(this.filters.level)]
            : [],
        trimesters: this.filters.trimester ? [String(this.filters.trimester)] : ["1"],
        subjectIds: [],
      };
      this.cloneSourceConfigSubjects = [];
      this.cloneTargetConfigSubjects = [];
      await this.fetchSchoolYears();
      this.$nextTick(() => {
        this.loadCloneSourceSubjects();
        this.loadCloneTargetSubjects();
      });
    },
    handleCloneCancel() {
      this.visibleCloneModal = false;
      this.cloneSource = {
        level: null,
        trimester: "1",
        subjectId: null,
        schoolYear: null,
      };
      this.cloneTarget = {
        schoolYearIds: [],
        levels: [],
        trimesters: [],
        subjectIds: [],
      };
      this.cloneSourceConfigSubjects = [];
      this.cloneTargetConfigSubjects = [];
    },
    async selectAllCloneTargets() {
      if (this.isReadOnly) return;
      const levelKeys = Object.keys(this.levelSelectData || {}).map(String);
      if (!levelKeys.length) {
        this.$message.warning(this.$t("appreciation.cloneSelectAllNoLevels"));
        return;
      }
      const trimesters = ["1", "2", "3"];
      this.selectingAllCloneTargets = true;
      try {
        const tasks = [];
        for (const level of levelKeys) {
          for (const trim of trimesters) {
            tasks.push(
              apiClient.post("/bulletin/college/config/subjects", {
                level: Number(level),
                trimester: Number(trim),
              })
            );
          }
        }
        const results = await Promise.all(tasks);
        const merged = this.mergeConfigSubjectRows(results);
        const sourceSid =
          this.cloneSource.subjectId != null
            ? String(this.cloneSource.subjectId)
            : null;
        const filtered = merged.filter((row) => {
          const sid = row.subject && (row.subject._id || row.subject);
          if (!sid) return false;
          const key = String(sid);
          return !sourceSid || key !== sourceSid;
        });
        const ids = filtered
          .map((row) => this.cloneObjectIdString(row.subject && (row.subject._id || row.subject)))
          .filter(Boolean);
        if (!ids.length) {
          this.$message.warning(this.$t("appreciation.cloneSelectAllNoSubjects"));
          return;
        }
        this.cloneTarget.levels = [...levelKeys];
        this.cloneTarget.trimesters = [...trimesters];
        this.cloneTargetConfigSubjects = filtered;
        this.cloneTarget.subjectIds = ids;
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.selectingAllCloneTargets = false;
      }
    },
    async executeClone() {
      if (
        !this.cloneSource.subjectId ||
        !this.cloneSource.schoolYear ||
        !this.cloneSource.trimester ||
        this.cloneSource.level == null ||
        this.cloneSource.level === ""
      ) {
        this.$message.warning(this.$t("appreciation.cloneCollegeSourceIncomplete"));
        return;
      }
      if (!this.cloneTarget.schoolYearIds || !this.cloneTarget.schoolYearIds.length) {
        this.$message.warning(this.$t("appreciation.selectTargetSchoolYear"));
        return;
      }
      if (!this.cloneTarget.trimesters || !this.cloneTarget.trimesters.length) {
        this.$message.warning(this.$t("appreciation.selectTargetTrimesters"));
        return;
      }
      if (!this.cloneTarget.levels || !this.cloneTarget.levels.length) {
        this.$message.warning(this.$t("appreciation.selectTargetLevels"));
        return;
      }
      if (!this.cloneTarget.subjectIds || this.cloneTarget.subjectIds.length === 0) {
        this.$message.warning(this.$t("appreciation.selectTargetSubjects"));
        return;
      }
      const targetSchoolYearIds = this.cloneTarget.schoolYearIds
        .map((id) => this.cloneObjectIdString(id))
        .filter(Boolean);
      const targetSubjectIds = this.cloneTarget.subjectIds
        .map((id) => this.cloneObjectIdString(id))
        .filter(Boolean);
      this.cloning = true;
      try {
        await apiClient.post(`${BASE}/clone`, {
          sourceSubjectId: this.cloneObjectIdString(this.cloneSource.subjectId),
          sourceSchoolYearId: this.cloneObjectIdString(this.cloneSource.schoolYear),
          sourceTrimester: Number(this.cloneSource.trimester),
          sourceLevel: Number(this.cloneSource.level),
          targetSchoolYearIds,
          targetTrimesters: this.cloneTarget.trimesters.map(Number),
          targetLevels: this.cloneTarget.levels.map(Number),
          targetSubjectIds,
        });
        this.$message.success(this.$t("action.cloneSuccess"));
        const activeYear = this.cloneObjectIdString(this.settings.activeSchoolarYear);
        const shouldReloadList =
          this.hasValidFilters &&
          activeYear &&
          targetSchoolYearIds.some((y) => this.cloneObjectIdString(y) === activeYear);
        this.handleCloneCancel();
        if (shouldReloadList) {
          await this.loadAutoAppreciations();
        }
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("appreciation.noAutoAppreciationToClone"));
      } finally {
        this.cloning = false;
      }
    },
    handleApiError(error, defaultMessage) {
      const errorCode =
        error.response?.data?.errorCode || error.response?.data?.error?.errorCode;
      if (errorCode && this.$te(`errorCode.${errorCode}`)) {
        this.$message.error(this.$t(`errorCode.${errorCode}`));
        return;
      }
      this.$message.error(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          defaultMessage ||
          this.$t("error.erreur")
      );
    },
    filterOption(input, option) {
      const c = option.componentOptions && option.componentOptions.children;
      if (!c || !c[0]) return false;
      const t = (c[0].text || c[0].elm && c[0].elm.textContent || "").toLowerCase();
      return t.indexOf(input.toLowerCase()) >= 0;
    },
    async loadConfigSubjects() {
      if (!this.filters.level || !this.filters.trimester) return;
      this.loadingSubjects = true;
      try {
        const res = await apiClient.post("/bulletin/college/config/subjects", {
          level: Number(this.filters.level),
          trimester: this.filters.trimester ? Number(this.filters.trimester) : null,
        });
        this.configSubjects = res.data || [];
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
        this.configSubjects = [];
      } finally {
        this.loadingSubjects = false;
      }
    },
    handleLevelChange() {
      this.filters.subjectId = null;
      this.loadConfigSubjects();
    },
    handleTrimesterChange() {
      this.filters.subjectId = null;
      this.loadConfigSubjects();
    },
    handleSubjectChange() {
      if (this.filters.subjectId && this.settings.activeSchoolarYear) {
        this.loadAutoAppreciations();
      } else {
        this.appreciations = [];
      }
    },
    handlePredefinedChange(value) {
      if (value) {
        this.form.setFieldsValue({ appreciationCustom: "" });
      }
    },
    handleCustomChange(e) {
      if (e.target.value) {
        this.form.setFieldsValue({ appreciationPredefined: null });
      }
    },
    async loadAutoAppreciations() {
      if (!this.filters.subjectId || !this.settings.activeSchoolarYear) {
        this.appreciations = [];
        return;
      }
      this.loading = true;
      try {
        const payload = {
          subjectId: this.filters.subjectId,
          schoolYearId: this.settings.activeSchoolarYear,
          trimester: Number(this.filters.trimester),
        };
        if (this.filters.level != null && this.filters.level !== "") {
          payload.level = Number(this.filters.level);
        }
        const response = await apiClient.post(`${BASE}/list`, payload);
        this.appreciations = (response.data && response.data.data) || [];
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("error.erreur"));
        this.appreciations = [];
      } finally {
        this.loading = false;
      }
    },
    showAddModal() {
      if (!this.hasValidFilters) {
        this.$message.warning(this.$t("all.fillFilters"));
        return;
      }
      this.editingAppreciation = null;
      this.visibleModal = true;
      this.$nextTick(() => this.form.resetFields());
    },
    showEditModal(record) {
      this.editingAppreciation = record;
      this.visibleModal = true;
      this.$nextTick(() => {
        this.form.setFieldsValue({
          min: record.min,
          max: record.max,
          appreciation: record.appreciation,
        });
      });
    },
    handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields(async (err, values) => {
        if (err) return;
        const appreciation =
          values.appreciationPredefined || values.appreciationCustom || "";
        if (!appreciation) {
          this.$message.warning(this.$t("appreciation.appreciation") + " " + this.$t("requis.required"));
          return;
        }
        this.saving = true;
        try {
          const payload = {
            subjectId: this.filters.subjectId,
            schoolYearId: this.settings.activeSchoolarYear,
            trimester: Number(this.filters.trimester),
            min: values.min,
            max: values.max,
            appreciation,
          };
          if (this.filters.level != null && this.filters.level !== "") {
            payload.level = Number(this.filters.level);
          }
          if (this.editingAppreciation && this.editingAppreciation._id) {
            await apiClient.patch(`${BASE}/${this.editingAppreciation._id}`, {
              min: values.min,
              max: values.max,
              appreciation,
            });
            this.$message.success(this.$t("success.appreciationOptionMSA"));
          } else {
            await apiClient.put(BASE + "/", payload);
            this.$message.success(this.$t("success.appreciationOptionAjout"));
          }
          this.handleCancel();
          await this.loadAutoAppreciations();
        } catch (error) {
          console.error(error);
          this.handleApiError(error, this.$t("error.erreur"));
        } finally {
          this.saving = false;
        }
      });
    },
    handleCancel() {
      this.visibleModal = false;
      this.editingAppreciation = null;
      this.form.resetFields();
    },
    async removeAppreciation(id) {
      try {
        await apiClient.delete(`${BASE}/${id}`);
        this.$message.success(this.$t("success.appreciationOptionSupp"));
        await this.loadAutoAppreciations();
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("error.erreur"));
      }
    },
    async applyAutomaticAppreciation() {
      if (!this.hasValidFilters || this.appreciations.length === 0) return;
      this.loadingExisting = true;
      this.visibleExistingModal = true;
      this.selectedExistingIds = [];
      try {
        const res = await apiClient.post(`${BASE}/check-existent`, {
          level: Number(this.filters.level),
          trimester: Number(this.filters.trimester),
          schoolYearId: this.settings.activeSchoolarYear,
          subjectId: this.filters.subjectId,
        });
        this.existingAppreciations = res.data.data || [];
      } catch (error) {
        console.error(error);
        this.handleApiError(error, this.$t("error.erreur"));
        this.visibleExistingModal = false;
      } finally {
        this.loadingExisting = false;
      }
    },
    onSelectExisting(selectedRowKeys) {
      this.selectedExistingIds = selectedRowKeys;
    },
    handleCancelExistingModal() {
      this.visibleExistingModal = false;
      this.existingAppreciations = [];
      this.selectedExistingIds = [];
    },
    async confirmApplyAppreciations() {
      if (!this.hasValidFilters || this.appreciations.length === 0) {
        this.$message.warning(this.$t("all.fillFilters"));
        return;
      }
      this.applying = true;
      try {
        const ignoredAppreciationIds = this.existingAppreciations
          .map((a) => a._id)
          .filter((id) => !this.selectedExistingIds.includes(id));
        const applyRes = await apiClient.post(`${BASE}/apply`, {
          level: Number(this.filters.level),
          trimester: Number(this.filters.trimester),
          schoolYearId: this.settings.activeSchoolarYear,
          subjectId: this.filters.subjectId,
          ignoredAppreciationIds,
        });
        const applied =
          applyRes.data && applyRes.data.count != null
            ? Number(applyRes.data.count)
            : 0;
        if (applied > 0) {
          this.$message.success(
            this.$t("appreciation.applySuccess", { count: applied })
          );
        } else {
          this.$message.info(this.$t("appreciation.applyNone"));
        }
        this.handleCancelExistingModal();
      } catch (error) {
        if (error.response && error.response.status === 501) {
          this.$message.warning(this.$t("appreciation.applyNotImplemented"));
          this.handleCancelExistingModal();
        } else {
          console.error(error);
          this.handleApiError(error, this.$t("error.erreur"));
        }
      } finally {
        this.applying = false;
      }
    },
  },
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 8px;
}
.clone-target-select-all {
  padding-top: 30px;
}
</style>
